import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  ArrowLeft, 
  Bell, 
  Volume2, 
  VolumeX,
  Eye,
  Building2,
  Download,
  Shield,
  Pencil,
  Trash2,
  Clock,
  X,
  Sun,
  Moon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

import { BACKEND_URL } from '@/config/api';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [isLoading, setIsLoading] = useState(false);
  const [hubs, setHubs] = useState([]);
  const [selectedExportHub, setSelectedExportHub] = useState('');
  
  // Edit workspace modal state
  const [isEditWorkspaceOpen, setIsEditWorkspaceOpen] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState(null);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceLogo, setWorkspaceLogo] = useState(null);
  const [workspaceColor, setWorkspaceColor] = useState('#3B82F6');
  
  const brandColors = [
    '#3B82F6', // Blue
    '#6366F1', // Indigo
    '#22C55E', // Green
    '#F97316', // Orange
    '#EAB308', // Yellow
    '#06B6D4', // Cyan
    '#EC4899', // Pink
    '#8B5CF6', // Purple
  ];
  
  // Notification settings
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    mentionNotifications: true,
    messageSound: true,
    callRingtone: true,
    doNotDisturb: false,
    scheduledQuietHours: false,
    volume: 50
  });

  useEffect(() => {
    fetchHubs();
  }, []);

  const fetchHubs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/hubs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Only get the primary workspace (the one ending with "'s Workspace")
        const ownedHubs = data.filter(h => h.owner_id === user?.id);
        const primaryWorkspace = ownedHubs.find(h => h.name?.includes("'s Workspace"));
        setHubs(primaryWorkspace ? [primaryWorkspace] : (ownedHubs.length > 0 ? [ownedHubs[0]] : []));
      }
    } catch (error) {
      console.error('Error fetching hubs:', error);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleVolumeChange = (e) => {
    setSettings(prev => ({
      ...prev,
      volume: parseInt(e.target.value)
    }));
  };

  const handleTestSound = () => {
    // Play a test sound
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQAA');
    audio.volume = settings.volume / 100;
    audio.play().catch(() => {});
  };

  const handleEnableBrowserNotifications = () => {
    if ('Notification' in window) {
      Notification.requestPermission();
    }
  };

  const openEditWorkspace = (workspace) => {
    setEditingWorkspace(workspace);
    setWorkspaceName(workspace.name || '');
    setWorkspaceLogo(workspace.logo || null);
    setWorkspaceColor(workspace.color || '#3B82F6');
    setIsEditWorkspaceOpen(true);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setWorkspaceLogo(e.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setWorkspaceLogo(null);
  };

  const handleSaveWorkspace = async () => {
    if (!editingWorkspace || !workspaceName.trim()) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/hubs/${editingWorkspace.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: workspaceName,
          color: workspaceColor,
          logo: workspaceLogo
        })
      });
      
      if (response.ok) {
        // Update local state
        setHubs(prev => prev.map(h => 
          h.id === editingWorkspace.id 
            ? { ...h, name: workspaceName, color: workspaceColor, logo: workspaceLogo }
            : h
        ));
        setIsEditWorkspaceOpen(false);
        setEditingWorkspace(null);
      } else {
        alert('Failed to update workspace');
      }
    } catch (error) {
      console.error('Error updating workspace:', error);
      alert('An error occurred while updating workspace');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportPersonalData = () => {
    alert('Personal data export will be sent to your email.');
  };

  const handleExportHubData = () => {
    if (!selectedExportHub) {
      alert('Please select a hub first');
      return;
    }
    alert('Hub data export will be sent to your email.');
  };

  const handleChangePassword = () => {
    alert('Password change functionality coming soon');
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      alert('Account deletion request submitted.');
    }
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      // Save settings to backend
      await new Promise(resolve => setTimeout(resolve, 500));
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 overflow-x-hidden">
      {/* Back Button */}
      <div className="max-w-3xl mx-auto pt-4 sm:pt-8 px-4 sm:px-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </button>

        {/* Notifications Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Bell size={20} className="text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">Manage how you receive notifications</p>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-gray-900 font-medium">Email Notifications</p>
                <p className="text-sm text-gray-500">Receive email updates about activity</p>
              </div>
              <Switch 
                checked={settings.emailNotifications} 
                onCheckedChange={() => handleToggle('emailNotifications')} 
              />
            </div>
            
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-gray-900 font-medium">Push Notifications</p>
                <p className="text-sm text-gray-500">Get push notifications in your browser</p>
              </div>
              <Switch 
                checked={settings.pushNotifications} 
                onCheckedChange={() => handleToggle('pushNotifications')} 
              />
            </div>
            
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-gray-900 font-medium">Mention Notifications</p>
                <p className="text-sm text-gray-500">Get notified when someone mentions you</p>
              </div>
              <Switch 
                checked={settings.mentionNotifications} 
                onCheckedChange={() => handleToggle('mentionNotifications')} 
              />
            </div>
          </div>
        </div>

        {/* Sound Settings Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Volume2 size={20} className="text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Sound Settings</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">Control notification sounds and Do Not Disturb</p>

          {/* Volume */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3 mb-1">
              <Volume2 size={16} className="text-gray-600" />
              <p className="text-gray-900 font-medium">Volume</p>
            </div>
            <p className="text-sm text-gray-500 mb-3">Adjust the notification sound volume</p>
            <div className="flex items-center gap-4">
              <VolumeX size={16} className="text-gray-400" />
              <input
                type="range"
                min="0"
                max="100"
                value={settings.volume}
                onChange={handleVolumeChange}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
              <span className="text-sm text-gray-600 w-12">{settings.volume}%</span>
              <Button variant="outline" size="sm" onClick={handleTestSound}>
                ▶ Test
              </Button>
            </div>
          </div>

          {/* Sound Types */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3 mb-1">
              <Bell size={16} className="text-gray-600" />
              <p className="text-gray-900 font-medium">Sound Types</p>
            </div>
            <p className="text-sm text-gray-500 mb-3">Choose which notifications play sounds</p>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-700 font-medium text-sm">Message notifications</p>
                  <p className="text-xs text-gray-500">Play sound for new messages</p>
                </div>
                <Switch 
                  checked={settings.messageSound} 
                  onCheckedChange={() => handleToggle('messageSound')} 
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-700 font-medium text-sm">Call ringtone</p>
                  <p className="text-xs text-gray-500">Play ringtone for incoming calls</p>
                </div>
                <Switch 
                  checked={settings.callRingtone} 
                  onCheckedChange={() => handleToggle('callRingtone')} 
                />
              </div>
            </div>
          </div>

          {/* Browser Notifications */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3 mb-1">
              <Bell size={16} className="text-gray-600" />
              <p className="text-gray-900 font-medium">Browser Notifications</p>
            </div>
            <p className="text-sm text-gray-500 mb-3">Get notified even when the tab is inactive or browser is in the background</p>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-700 font-medium text-sm">Enable push notifications</p>
                <p className="text-xs text-gray-500">Click to allow browser notifications</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleEnableBrowserNotifications}>
                Enable
              </Button>
            </div>
          </div>

          {/* Do Not Disturb */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3 mb-1">
              <VolumeX size={16} className="text-gray-600" />
              <p className="text-gray-900 font-medium">Do Not Disturb</p>
            </div>
            <p className="text-sm text-gray-500 mb-3">Mute all notification sounds temporarily</p>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-700 font-medium text-sm">Enable Do Not Disturb</p>
                <p className="text-xs text-gray-500">Mute all sounds immediately</p>
              </div>
              <Switch 
                checked={settings.doNotDisturb} 
                onCheckedChange={() => handleToggle('doNotDisturb')} 
              />
            </div>
          </div>

          {/* Scheduled Quiet Hours */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-1">
              <Clock size={16} className="text-gray-600" />
              <p className="text-gray-900 font-medium">Scheduled Quiet Hours</p>
            </div>
            <p className="text-sm text-gray-500 mb-3">Automatically mute sounds during specific times</p>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-700 font-medium text-sm">Enable schedule</p>
                <p className="text-xs text-gray-500">Automatically enable DND at set times</p>
              </div>
              <Switch 
                checked={settings.scheduledQuietHours} 
                onCheckedChange={() => handleToggle('scheduledQuietHours')} 
              />
            </div>
          </div>
        </div>

        {/* Appearance Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Eye size={20} className="text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Appearance</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">Customize how BoardChat looks</p>

          <div className="flex items-center justify-between">
            <p className="text-gray-900 font-medium">Theme</p>
            <div className="flex gap-2">
              {/* Light Mode Option */}
              <button
                onClick={() => toggleTheme('light')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  theme === 'light'
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <Sun size={16} />
                <span className="text-sm font-medium">Light</span>
              </button>

              {/* Dark Mode Option */}
              <button
                onClick={() => toggleTheme('dark')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  theme === 'dark'
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <Moon size={16} />
                <span className="text-sm font-medium">Dark</span>
              </button>
            </div>
          </div>
        </div>

        {/* Workspace Management Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Building2 size={20} className="text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Workspace</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">Your primary workspace. Hubs are created within this workspace.</p>

          <div className="space-y-3">
            {hubs.map((hub, index) => {
              const colors = ['#F97316', '#6366F1', '#8B5CF6', '#10B981', '#EC4899'];
              const color = colors[index % colors.length];
              return (
                <div key={hub.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: color }}
                    >
                      {hub.name?.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">{hub.name}</p>
                      <p className="text-xs text-gray-500">Owner</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEditWorkspace(hub)}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="destructive" size="sm">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
            {hubs.length === 0 && (
              <p className="text-gray-500 text-sm py-4 text-center">No workspaces found</p>
            )}
          </div>
        </div>

        {/* Data Export Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Download size={20} className="text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Data Export</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">Download your data for backup or portability (GDPR compliant)</p>

          {/* Personal Data Export */}
          <div className="mb-6">
            <p className="text-gray-900 font-medium">Personal Data Export</p>
            <p className="text-sm text-gray-500 mb-3">Download all your personal data including messages, profile, and activity</p>
            <Button variant="outline" onClick={handleExportPersonalData}>
              <Download size={16} className="mr-2" />
              Export Personal Data
            </Button>
          </div>

          {/* Hub Data Export */}
          <div>
            <p className="text-gray-900 font-medium">Hub Data Export</p>
            <p className="text-sm text-gray-500 mb-3">Download all data from a hub you own or administer (boards, messages, files)</p>
            <div className="flex items-center gap-3">
              <select
                value={selectedExportHub}
                onChange={(e) => setSelectedExportHub(e.target.value)}
                className="h-10 px-3 rounded-md border border-gray-200 bg-white text-gray-900 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Select a hub</option>
                {hubs.map(hub => (
                  <option key={hub.id} value={hub.id}>{hub.name}</option>
                ))}
              </select>
              <Button variant="outline" onClick={handleExportHubData}>
                <Download size={16} className="mr-2" />
                Export Hub Data
              </Button>
            </div>
          </div>
        </div>

        {/* Privacy & Security Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Shield size={20} className="text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Privacy & Security</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">Manage your account security settings</p>

          <div className="mb-6">
            <Button variant="outline" onClick={handleChangePassword}>
              Change Password
            </Button>
          </div>

          <div>
            <p className="text-gray-900 font-medium">Delete Account</p>
            <p className="text-sm text-gray-500 mb-3">Permanently delete your account and all associated data</p>
            <Button variant="destructive" onClick={handleDeleteAccount}>
              Delete Account
            </Button>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSaveSettings}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6"
          >
            {isLoading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Edit Workspace Dialog */}
      <Dialog open={isEditWorkspaceOpen} onOpenChange={setIsEditWorkspaceOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Workspace</DialogTitle>
            <DialogDescription className="text-gray-500">
              Update your workspace name and logo.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Workspace Logo */}
            <div>
              <Label className="text-gray-700 mb-3 block">Workspace Logo</Label>
              <div className="flex items-center gap-4">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-xl font-bold overflow-hidden border-2 border-dashed border-gray-300"
                  style={{ backgroundColor: workspaceLogo ? 'transparent' : workspaceColor }}
                >
                  {workspaceLogo ? (
                    <img src={workspaceLogo} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    workspaceName?.substring(0, 2).toUpperCase() || 'WS'
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <span className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                      Upload image
                    </span>
                  </label>
                  {workspaceLogo && (
                    <button 
                      onClick={handleRemoveLogo}
                      className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500"
                    >
                      <X size={14} />
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Brand Color */}
            <div>
              <Label className="text-gray-700 mb-3 block">Brand Color</Label>
              <div className="flex items-center gap-2">
                {brandColors.map(color => (
                  <button
                    key={color}
                    onClick={() => setWorkspaceColor(color)}
                    className={`w-8 h-8 rounded-full transition-all ${
                      workspaceColor === color 
                        ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' 
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* Workspace Name */}
            <div>
              <Label className="text-gray-700 mb-2 block">Workspace Name</Label>
              <Input
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                placeholder="Enter workspace name"
                className="border-gray-200 focus:border-blue-500"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditWorkspaceOpen(false);
                setEditingWorkspace(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveWorkspace}
              disabled={!workspaceName.trim() || isLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isLoading ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
